import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { PrismCheckbox, PrismIcon } from '@mylighthouse/prism-ember';
import type { BudgetForecastSegment } from 'lumen/models';

import styles from './item.module.css';

interface SettingsBudgetForecastSegmentHierarchyItemSignature {
  Args: {
    isChecked?: boolean;
    isExpanded?: boolean;
    onExpandSegment: () => void;
    onToggleSegmentCheckbox: () => void;
    segment: BudgetForecastSegment;
  };
}

export default class SettingsBudgetForecastSegmentHierarchyItem extends Component<SettingsBudgetForecastSegmentHierarchyItemSignature> {
  get isDisabled(): boolean {
    return this.args.segment.children.length === 0;
  }

  @action
  onExpandSegment(event: MouseEvent): void {
    if (
      !event.target ||
      (event.target as HTMLElement).closest('[data-checkbox]') !== null
    ) {
      return;
    }

    this.args.onExpandSegment();
  }

  <template>
    {{! template-lint-disable no-nested-interactive }}
    <li
      aria-expanded={{@isExpanded}}
      aria-selected={{@isChecked}}
      role="treeitem"
    >
      <button
        aria-label="{{if @isExpanded 'Collapse' 'Expand'}} {{@segment.name}}"
        class={{concat
          "button--unstyled"
          " "
          styles.item
          " "
          (if @isExpanded styles.item--expanded)
          (if this.isDisabled styles.item--disabled)
        }}
        data-test-id="segment-hierarchy-item"
        disabled={{this.isDisabled}}
        type="button"
        {{on "click" this.onExpandSegment}}
      >
        <div
          class="text-200--regular flex items-center gap-200 overflow-hidden py-200"
        >
          <div data-checkbox>
            <PrismCheckbox
              @checked={{@isChecked}}
              @isSmall={{true}}
              @onChange={{@onToggleSegmentCheckbox}}
              aria-label="{{if @isChecked 'Remove' 'Add'}} {{@segment.name}}"
              data-test-id="segment-hierarchy-item-checkbox"
            />
          </div>

          <p class="truncate" title={{@segment.name}}>
            {{@segment.name}}
          </p>
        </div>

        {{#if @segment.children}}
          <PrismIcon
            @name="chevron-right"
            @size="large"
            aria-hidden="true"
            data-test-id="segment-hierarchy-item-icon"
          />
        {{/if}}
      </button>
    </li>
  </template>
}
